import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const WAVESHAPER_MODES = Object.freeze([
  'tanh',
  'atan',
  'polynomial',
  'diode-like',
  'fold',
  'asymmetric',
] as const);
export type WaveshaperMode = (typeof WAVESHAPER_MODES)[number];

export const OVERSAMPLING_FACTORS = Object.freeze([1, 2, 4] as const);
export type OversamplingFactor = (typeof OVERSAMPLING_FACTORS)[number];

export interface WaveshaperOptions {
  readonly sampleRate: number;
  readonly mode?: WaveshaperMode;
  readonly drive?: number;
  readonly bias?: number;
  readonly mix?: number;
  readonly output?: number;
  readonly dcBlocker?: boolean;
  readonly oversampling?: OversamplingFactor;
}

const DC_BLOCKER_HZ = 10;
const DENORMAL_LIMIT = 1e-30;

const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
};

const flush = (value: number): number => Math.abs(value) < DENORMAL_LIMIT ? 0 : value;
const clamp = (value: number, minimum: number, maximum: number): number =>
  value < minimum ? minimum : value > maximum ? maximum : value;

const fold = (value: number): number => {
  const phase = ((value + 1) % 4 + 4) % 4;
  return phase <= 2 ? phase - 1 : 3 - phase;
};

/** A bounded, memoryless transfer curve. The DC blocker and oversampler live outside this function. */
export const evaluateWaveshaperCurve = (mode: WaveshaperMode, value: number): number => {
  if (!Number.isFinite(value)) throw new RangeError('curve input must be finite');
  switch (mode) {
    case 'tanh':
      return Math.tanh(value);
    case 'atan':
      return Math.atan(value) / (Math.PI * 0.5);
    case 'polynomial': {
      if (value <= -1) return -1;
      if (value >= 1) return 1;
      return 1.5 * (value - value * value * value / 3);
    }
    case 'diode-like': {
      const magnitude = 1 - Math.exp(-Math.abs(value));
      return value < 0 ? -magnitude : magnitude;
    }
    case 'fold':
      return fold(value);
    case 'asymmetric':
      return value >= 0
        ? Math.tanh(value)
        : 0.75 * Math.atan(value * 1.5) / (Math.PI * 0.5);
    default:
      throw new RangeError('mode is invalid');
  }
};

/**
 * Host-independent mono waveshaper. All hot-path state is scalar and all
 * buffers are caller-owned, so processInto performs no per-block allocation.
 */
export class Waveshaper {
  readonly sampleRate: number;
  private mode: WaveshaperMode;
  private drive: number;
  private bias: number;
  private mix: number;
  private output: number;
  private dcBlocker: boolean;
  private oversampling: OversamplingFactor;
  private previousInput = 0;
  private previousWet = 0;
  private previousBlocked = 0;
  private readonly dcCoefficient: number;

  constructor(options: WaveshaperOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000);
    this.sampleRate = options.sampleRate;
    this.mode = options.mode ?? 'tanh';
    this.drive = options.drive ?? 1;
    this.bias = options.bias ?? 0;
    this.mix = options.mix ?? 1;
    this.output = options.output ?? 1;
    this.dcBlocker = options.dcBlocker ?? true;
    this.oversampling = options.oversampling ?? 2;
    this.validateMode(this.mode);
    this.validateOversampling(this.oversampling);
    assertRange('drive', this.drive, 0.25, 32);
    assertRange('bias', this.bias, -1, 1);
    assertRange('mix', this.mix, 0, 1);
    assertRange('output', this.output, 0, 2);
    this.dcCoefficient = Math.exp(-2 * Math.PI * DC_BLOCKER_HZ / this.sampleRate);
  }

  reset(): void {
    this.previousInput = 0;
    this.previousWet = 0;
    this.previousBlocked = 0;
  }

  setParameter(id: string, value: number): void {
    if (id === 'mode') {
      if (!Number.isInteger(value) || value < 0 || value >= WAVESHAPER_MODES.length) {
        throw new RangeError('mode must be an integer in [0, 5]');
      }
      this.mode = WAVESHAPER_MODES[value]!;
      return;
    }
    if (id === 'drive') { assertRange(id, value, 0.25, 32); this.drive = value; return; }
    if (id === 'bias') { assertRange(id, value, -1, 1); this.bias = value; return; }
    if (id === 'mix') { assertRange(id, value, 0, 1); this.mix = value; return; }
    if (id === 'output') { assertRange(id, value, 0, 2); this.output = value; return; }
    if (id === 'dc-blocker') {
      if (value !== 0 && value !== 1) throw new RangeError('dc-blocker must be 0 or 1');
      this.dcBlocker = value === 1;
      return;
    }
    if (id === 'oversampling') {
      this.validateOversampling(value);
      this.oversampling = value;
      return;
    }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  processInto(input: Float32Array, output: Float32Array, frames = output.length): void {
    if (!Number.isInteger(frames) || frames < 0 || input.length < frames || output.length < frames) {
      throw new RangeError('buffers are too short for frames');
    }
    for (let index = 0; index < frames; index += 1) {
      if (!Number.isFinite(input[index]!)) throw new RangeError('input samples must be finite');
    }
    const factor = this.oversampling;
    for (let index = 0; index < frames; index += 1) {
      const dry = input[index]!;

      let accumulated = 0;
      const delta = dry - this.previousInput;
      for (let phase = 1; phase <= factor; phase += 1) {
        const interpolated = this.previousInput + delta * phase / factor;
        accumulated += evaluateWaveshaperCurve(this.mode, (interpolated + this.bias) * this.drive);
      }
      this.previousInput = dry;
      let wet = accumulated / factor;

      if (this.dcBlocker) {
        const blocked = wet - this.previousWet + this.dcCoefficient * this.previousBlocked;
        this.previousWet = wet;
        this.previousBlocked = flush(blocked);
        wet = this.previousBlocked;
      } else {
        this.previousWet = wet;
        this.previousBlocked = 0;
      }

      const mixed = dry + (wet - dry) * this.mix;
      output[index] = flush(clamp(mixed, -1, 1) * this.output);
    }
  }

  private validateMode(mode: string): asserts mode is WaveshaperMode {
    if (!(WAVESHAPER_MODES as readonly string[]).includes(mode)) throw new RangeError('mode is invalid');
  }

  private validateOversampling(value: number): asserts value is OversamplingFactor {
    if (!(OVERSAMPLING_FACTORS as readonly number[]).includes(value)) {
      throw new RangeError('oversampling must be 1, 2 or 4');
    }
  }
}

class WaveshaperModuleInstance implements DspModuleInstance {
  private readonly shaper: Waveshaper;

  constructor(sampleRate: number, private readonly maxBlockSize: number) {
    this.shaper = new Waveshaper({ sampleRate });
  }

  reset(): void { this.shaper.reset(); }
  setParameter(id: string, value: number): void { this.shaper.setParameter(id, value); }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
    const input = block.inputs.in;
    const output = block.outputs.out;
    if (!input || input.length < block.frames) throw new RangeError('in buffer is missing or too short');
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    this.shaper.processInto(input, output, block.frames);
  }
}

export const mod023 = defineDspModule({
  descriptor: {
    id: 'mir.mod-023',
    name: 'Waveshaper and saturation',
    version: '0.1.0',
    inputs: [{ id: 'in', kind: 'audio', channels: 1 }],
    outputs: [{ id: 'out', kind: 'audio', channels: 1 }],
    parameters: [
      { id: 'mode', defaultValue: 0, minValue: 0, maxValue: 5, unit: 'enum' },
      { id: 'drive', defaultValue: 1, minValue: 0.25, maxValue: 32, unit: 'ratio' },
      { id: 'bias', defaultValue: 0, minValue: -1, maxValue: 1, unit: 'linear' },
      { id: 'mix', defaultValue: 1, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'output', defaultValue: 1, minValue: 0, maxValue: 2, unit: 'linear' },
      { id: 'dc-blocker', defaultValue: 1, minValue: 0, maxValue: 1, unit: 'boolean' },
      { id: 'oversampling', defaultValue: 2, minValue: 1, maxValue: 4, unit: 'factor' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate <= 0 ||
      !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new WaveshaperModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});